import { mapDataToSelectData } from '../../../vue/src/api/Helpers'

/* Usage
  // -------------------------------------------------------- Scenario 1: Hardcoded Options
  <div
    data-crm-component="select"
    class="required" <!-- optional -->
    class="inline" <!-- optional -->
  >
    <label>Label text here</label>
    <select>
      <option value="null" selected>Please select...</option>
      <option value="1">Name of option 1</option>
      <option value="2" disabled>Name of option 2 (disabled)</option>
      <optgroup label="Test group">
        <option value="3">Name of option 3</option>
      </optgroup>
    </select>
    <div class="validation">
      <span class="required">This field is required</span>
    </div>
  </div>

  // -------------------------------------------------------- Scenario 2: Options Loaded Dynamically
  <div
    data-crm-component="select"
    data-select-data="unloaded" <!-- required as fetch handles the state, data attr is used for styling too -->
    data-select-endpoint="/IamAPI/user/vendor/GetDataForDropdowns" <!-- required, insert absolute url of where to fetch -->
    data-option-properties="["Id", "Name"]" <!-- required, first is the value property, second is the label property, used so the end point can be ambiguous -->

    class="required" <!-- optional -->
    class="inline" <!-- optional -->
  >
    <label>Load data on interaction</label>
    <select>
      <option value="0" selected></option> <!-- if value is blank / not loaded -->
      <option value="8" selected>Mr &amp; Mrs</option> <!-- if value is retrieved on load etc. -->
    </select>
  </div>

  // -------------------------------------------------------- Scenario 3: Searchable / Filterable Options
  // Scenario 3a: "search": Search to fetch (fetch is only executed when the user has typed 3 or more characters)
  <div
    data-crm-component="select"
    data-select-data="unloaded"
    data-select-endpoint="/IamAPI/user/vendor/GetDataForDropdowns"

    data-select-search="search" <!-- only unique one for this version, can either be 'search' or 'lazy' -->

    class="required"
  >
    <label>Data on search input</label>
    <select>
      <option selected value="4">Default value</option>
    </select>
    <div class="validation">
      <span class="min">Please enter at least 3 characters to search</span>
      <span class="required">This field is required</span>
    </div>
  </div>

  // Scenario 3b: "lazy": Search to filter (fetch is triggered in same as scenario 2, the search acts as a filter and attempts to insert the value automatically if search matches at least 1 option)
  <div
    data-crm-component="select"
    data-select-data="unloaded"
    data-select-endpoint="/IamAPI/user/vendor/GetDataForDropdowns"
    data-select-search="lazy"

    class="required"
  >
    <label>Data on interact, filter</label>
    <select>
      <option selected value="4">Default value</option>
    </select>
    <div class="validation">
      <span class="min">Please enter at least 3 characters to search</span>
      <span class="required">This field is required</span>
    </div>
  </div>
*/

document.addEventListener('DOMContentLoaded', () => {
  // #region <Vars>
  const crmSelects = document.querySelectorAll('[data-select-data="unloaded"]')
  const crmSearchableSelects = document.querySelectorAll('[data-select-search]')
  const crmRequiredSelects = Array.from(document.querySelectorAll('[data-crm-component="select"].required select'))
    .filter(element => element instanceof HTMLSelectElement) as HTMLSelectElement[]
  const crmFilterableSelects = document.querySelectorAll('[data-crm-component][data-crm-filterable]')
  // #endregion

  // #region <initialise selects that aren't searchable selects>
  crmSelects.forEach((crmSelect) => {
    const isSearchable = (!!crmSelect.getAttribute('data-select-search')) // boolean

    if (!isSearchable) {
      crmSelect.addEventListener('click', () => {
        getCrmSelectData(crmSelect)
      })
    };
  })

  crmFilterableSelects.forEach((crmFilterableSelect) => {
    const crmSelectElement = crmFilterableSelect.querySelector('select')

    const searchInput = document.createElement('input') // create input
    searchInput.placeholder = 'Filter options'
    searchInput.type = 'text'
    searchInput.classList.add('search-input')

    crmFilterableSelect.appendChild(searchInput)

    crmFilterableSelect.addEventListener('click', async () => {
      searchInput.classList.add('vis')
    })

    searchInput.addEventListener('input', () => {
      const options = crmFilterableSelect.querySelectorAll('option') as NodeListOf<HTMLOptionElement>
      const filterLower = searchInput.value.toLowerCase()

      options.forEach((option) => {
        if (option.textContent?.toLowerCase().includes(filterLower))
          option.hidden = false // Show the option

        else
          option.hidden = true // Hide the option
      })

      const firstVisibleOption = Array.from(options).find(
        option => !option.hidden,
      )

      // Set the first visible option as the selected option
      if (firstVisibleOption) {
        firstVisibleOption.selected = true
      }
      else {
        if (crmSelectElement) {
          crmSelectElement.value = 'null'
          crmSelectElement.selectedIndex = -1 // No selection
        }
      }

      crmSelectElement?.dispatchEvent(new Event('change')) // Dispatch the change event
    })
  })
  // #endregion

  // #region <initialise selects with search functionality>
  crmSearchableSelects.forEach((crmSearchableSelect) => {
    const crmSearchableSelectElement = crmSearchableSelect.querySelector('select')

    const searchInput = document.createElement('input') // create input
    searchInput.type = 'text'
    searchInput.classList.add('search-input')
    crmSearchableSelect.appendChild(searchInput)

    const searchMode = crmSearchableSelect.getAttribute('data-select-search') // 'search' | 'lazy'

    // dynamic search to pass to end point
    if (searchMode === 'search') {
      crmSearchableSelect.addEventListener('click', () => {
        if (crmSearchableSelect.getAttribute('data-select-data') !== 'unloaded')
          return // already loaded

        searchInput.placeholder = 'Search for options'
        searchInput.classList.add('vis')
        searchInput.focus()
      })

      let debounceTimer: any
      searchInput.addEventListener('input', () => {
        clearTimeout(debounceTimer)

        crmSearchableSelect.setAttribute('data-select-data', 'unloaded') // set attribute to unloaded if search changes
        crmSearchableSelect.classList.add('invalid')
        crmSearchableSelect.querySelector('.validation .min')?.classList.add('vis')

        if (searchInput && searchInput.value.length > 2) {
          crmSearchableSelect.classList.remove('invalid')
          crmSearchableSelect.querySelector('.validation .min')?.classList.remove('vis')
          debounceTimer = setTimeout(() => {
            getCrmSelectData(crmSearchableSelect)
          }, 400)
        }
      })
    }
    else if (searchMode === 'lazy') { // load all options and use frontend filtering
      crmSearchableSelect.addEventListener('click', async () => {
        if (crmSearchableSelect.getAttribute('data-select-data') !== 'unloaded')
          return // already loaded

        await getCrmSelectData(crmSearchableSelect)

        searchInput.placeholder = 'Filter options'
        searchInput.classList.add('vis')
        searchInput.focus()
      })

      searchInput.addEventListener('input', () => {
        const options = crmSearchableSelect.querySelectorAll('option') as NodeListOf<HTMLOptionElement>
        const filterLower = searchInput.value.toLowerCase()

        options.forEach((option) => {
          if (option.textContent?.toLowerCase().includes(filterLower))
            option.hidden = false // Show the option

          else
            option.hidden = true // Hide the option
        })

        const firstVisibleOption = Array.from(options).find(
          option => !option.hidden,
        )

        // Set the first visible option as the selected option
        if (firstVisibleOption) {
          firstVisibleOption.selected = true
        }
        else {
          if (crmSearchableSelectElement) {
            crmSearchableSelectElement.value = 'null'
            crmSearchableSelectElement.selectedIndex = -1 // No selection
          }
        }

        crmSearchableSelectElement?.dispatchEvent(new Event('change')) // Dispatch the change event
      })
    }
  })
  // #endregion

  // #region <validation on each required field>
  crmRequiredSelects.forEach((crmSelect) => {
    crmSelect.addEventListener('change', () => {
      const wrapper = crmSelect.parentNode as HTMLDivElement

      if (crmSelect.value === 'null' || crmSelect.value === '') {
        wrapper.classList.add('invalid')
        wrapper.classList.remove('valid')
        wrapper.querySelector('.validation .required')?.classList.add('vis')
      }
      else {
        wrapper.classList.remove('invalid')
        wrapper.classList.add('valid')
        wrapper.querySelector('.validation .required')?.classList.remove('vis')
      }
    })
  })
  // #endregion

  // #region <Get Data fetch>
  async function getCrmSelectData(element: Element) {
    const elSelect = element.querySelector('select') // actual select tag
    const elSearchInput = element.querySelector('input') // search input

    // const stringList = '["Id", "Name"]';
    // const array = JSON.parse(stringList);
    // console.log(array); // Output: ["Id", "Name"]

    if (!elSelect)
      return

    const url = element.getAttribute('data-select-endpoint') // string
    const isSearchable = (element.getAttribute('data-select-search') === 'search') // boolean

    const properties = element.getAttribute('data-option-properties') // '["Id", "Name"]'
    const propertiesArray = JSON.parse(properties || '')

    if (element.getAttribute('data-select-data') !== 'unloaded')
      return // already loaded

    if (url) {
      element.setAttribute('data-select-data', 'loading')
      elSelect.innerHTML = '<option disabled selected value="null">Loading data...</option>'

      try {
        let options

        if (isSearchable) {
          options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(elSearchInput?.value || ''),
          }
        }
        else {
          options = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          }
        }

        const response = await fetch(url, options)
        const data = await response.json()

        if (!response.ok || !(data.success || data.Success))
          throw new Error('Failed to get data')

        if (data.data) {
          const d = mapDataToSelectData(data.data, (propertiesArray[0] || 'Id'), (propertiesArray[1] || 'Name'))

          const fragment = document.createDocumentFragment()

          elSelect.innerHTML = '<option disabled selected value="null">Please select...</option>'

          d.forEach((opt: any) => {
            const option = document.createElement('option')
            option.value = opt.value
            option.textContent = opt.label
            fragment.appendChild(option)
          })

          elSelect.appendChild(fragment)

          element.setAttribute('data-select-data', 'loaded')

          elSelect.click()
        }
      }
      catch (error) {
        console.error(error)
      }
    } // /if url
  }
  // #endregion
})
